import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { isValidRoomId } from '@shared/schemas/validation';
import type { RoomInfo } from '@shared/types/room';
import { Spinner } from '@/components/Spinner';
import { StartOverlay } from '@/components/StartOverlay';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { ApiRequestError, api } from '@/services/api';
import { clearSession, loadSession } from '@/services/session';
import { useRoomStore } from '@/stores/roomStore';
import { ClosedView } from './room/ClosedView';
import { GameView } from './room/GameView';
import { JoinView } from './room/JoinView';
import { LobbyView } from './room/LobbyView';
import { ResultView } from './room/ResultView';

type Phase = 'loading' | 'not-found' | 'join' | 'connected';

export function RoomPage() {
  const { roomId = '' } = useParams();
  const validId = isValidRoomId(roomId);
  const [phase, setPhase] = useState<Phase>(validId ? 'loading' : 'not-found');
  const [info, setInfo] = useState<RoomInfo | null>(null);
  const [joinNotice, setJoinNotice] = useState<string | null>(null);

  const connect = useRoomStore((s) => s.connect);
  const reset = useRoomStore((s) => s.reset);
  const state = useRoomStore((s) => s.state);
  const connection = useRoomStore((s) => s.connection);
  const closedReason = useRoomStore((s) => s.closedReason);
  const reveal = useRoomStore((s) => s.reveal);

  useDocumentMeta({ title: state?.name ?? info?.name ?? '방', noIndex: true });

  // 방 정보 로드 + 기존 세션이 있으면 자동 재접속
  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    setPhase('loading');
    api
      .getRoomInfo(roomId)
      .then((roomInfo) => {
        if (cancelled) return;
        setInfo(roomInfo);
        const session = loadSession(roomId);
        if (session) {
          connect(roomId, session.participantId, session.token);
          setPhase('connected');
        } else {
          setPhase('join');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiRequestError && err.status === 404) {
          clearSession(roomId);
          setPhase('not-found');
        } else {
          setJoinNotice(err instanceof ApiRequestError ? err.message : '방 정보를 불러오지 못했어요.');
          setPhase('not-found');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, validId, connect]);

  // 페이지를 떠나면 소켓 정리
  useEffect(() => () => reset(), [reset]);

  // 인증 실패(세션 만료/제거됨)면 다시 입장 화면으로
  useEffect(() => {
    if (phase === 'connected' && connection === 'closed' && closedReason === 'unauthorized') {
      api
        .getRoomInfo(roomId)
        .then((roomInfo) => {
          setInfo(roomInfo);
          setJoinNotice('참여 정보가 만료되어 다시 입장해야 해요.');
          setPhase('join');
        })
        .catch(() => setPhase('not-found'));
    }
  }, [phase, connection, closedReason, roomId]);

  if (phase === 'not-found') {
    return <ClosedView reason="expired" roomId={roomId} message={joinNotice} />;
  }
  if (phase === 'loading' || !info) {
    return <Spinner label="방 정보를 불러오는 중..." />;
  }
  if (phase === 'join') {
    return (
      <JoinView
        roomId={roomId}
        info={info}
        notice={joinNotice}
        onJoined={(session) => {
          setJoinNotice(null);
          connect(roomId, session.participantId, session.token);
          setPhase('connected');
        }}
      />
    );
  }

  if (connection === 'closed' && closedReason && closedReason !== 'unauthorized') {
    return <ClosedView reason={closedReason} roomId={roomId} />;
  }
  if (!state) {
    return <Spinner label={connection === 'reconnecting' ? '다시 연결하는 중...' : '방에 연결하는 중...'} />;
  }

  const showGame = state.status === 'PLAYING' || (state.status === 'COMPLETED' && reveal !== null);

  return (
    <>
      {connection === 'reconnecting' && (
        <div className="mb-3 rounded-xl bg-amber-50 px-4 py-2 text-center text-sm text-amber-800" role="status">
          연결이 끊어졌어요. 다시 연결하는 중...
        </div>
      )}
      {(state.status === 'WAITING' || state.status === 'STARTING') && <LobbyView />}
      {state.status === 'STARTING' && <StartOverlay startsAt={state.startsAt} participantCount={state.maxParticipants} />}
      {showGame && <GameView />}
      {state.status === 'COMPLETED' && reveal === null && <ResultView />}
    </>
  );
}
